/**
 * SkeletonRow — placeholder for table/list rows.
 *
 * Renders `count` shimmer bars at row height with spacing between them.
 * Designed for DataTable isLoading=true usage.
 * Uses only CSS custom-property references.
 *
 * @see specs/integration-plan.md Section 5.1
 */
import React from "react";
import { useShimmer } from "./useShimmer";

const SKELETON_ANIMATION = "skel 1.2s ease-in-out infinite";

const SKELETON_GRADIENT =
  "linear-gradient(90deg, var(--bg2) 0%, rgba(0,0,0,0.06) 50%, var(--bg2) 100%)";

export interface SkeletonRowProps {
  /** Number of placeholder rows. Default: 1. */
  count?: number;
}

const ROW_HEIGHT = "var(--space-2xl, 40px)";
const ROW_GAP = "var(--space-sm, 8px)";

const shimmerBackground =
  SKELETON_GRADIENT;

export function SkeletonRow({
  count = 1,
}: SkeletonRowProps): React.ReactElement {
  const animate = useShimmer();
  const rows = Math.max(0, count);

  return (
    <div data-testid="skeleton-row" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          style={{
            height: ROW_HEIGHT,
            marginTop: i > 0 ? ROW_GAP : undefined,
            borderRadius: "var(--radius-sm, 4px)",
            background: shimmerBackground,
            backgroundSize: "200% 100%",
            animation: animate
              ? SKELETON_ANIMATION
              : "none",
          }}
        />
      ))}
    </div>
  );
}
